import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectAutomationRuleIdFromRoute } from 'src/app/UpdateAutomationRule/redux/updateAutomationRule.selectors'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import AutomationRuleForm from 'src/components/AutomationRuleForm/AutomationRuleForm'
import { EXPENSE_CATEGORY_PREDICT_OPTION } from 'src/components/ExpenseCategoryDropdown/ExpenseCategoryDropdown'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotFoundRightFlyoutContent from 'src/components/NotFoundRightFlyoutContent/NotFoundRightFlyoutContent'
import RightFlyout, {
  RightFlyoutBodyWidths,
} from 'src/components/RightFlyout/RightFlyout'
import { ImageNames } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import {
  PutAutomationRuleRequest,
  useAutomationRuleDetail,
  usePutAutomationRule,
} from 'src/queries/organizations/automationRules.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

const UpdateAutomationRule: React.FC = () => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const dispatch = useBackgroundDispatch()
  const { mutate: putAutomationRule, isPending, error } = usePutAutomationRule()
  const { data: currentUser, isPending: currentUserInitialLoading } =
    useQueryOwnUser({})
  const userCanViewPage = !!currentUser?.hasOrgBookkeeperPermissions
  const ruleId = useSelector(selectAutomationRuleIdFromRoute)
  const {
    data: _initialRule,
    isPending: initialRuleIsPending,
    error: ruleError,
  } = useAutomationRuleDetail({
    ruleId,
    enabled: userCanViewPage,
    retry: false,
  })

  // TODO: hack to handle prediction rules in the interim while we migrate to a better generic model
  const initialRule = useMemo(() => {
    if (!_initialRule) return

    _initialRule.mappings.forEach((mapping) => {
      if (mapping.expenseLabel?.id === EXPENSE_CATEGORY_PREDICT_OPTION) {
        mapping.expenseLabel.name = translateSingleString(
          locale,
          'Predict this label',
        )
      }
    })
    return _initialRule
  }, [_initialRule])

  const onSuccessfulSubmit = (body: PutAutomationRuleRequest): void => {
    putAutomationRule(
      { id: ruleId, body: body },
      { onSuccess: () => dispatch(closeModal()) },
    )
  }

  const imageSrc = useColorOrGrayscaleImage(ImageNames.CardTypeStandard)

  return (
    <RightFlyout width={RightFlyoutBodyWidths.Action}>
      {!userCanViewPage || ruleError ? (
        <NotFoundRightFlyoutContent />
      ) : (
        <ActionRightFlyoutContent
          title={translate('Update Expense Category Rule')}
          specificElements={{
            imageSrc: imageSrc,
          }}
          pickerSubtitle={translate(
            'Updating expense categories on your rule will only apply to future transactions. To update historical transactions, please use the transaction bulk update.',
          )}
        >
          {currentUserInitialLoading || initialRuleIsPending ? (
            <LoadingBackgroundPage />
          ) : (
            <AutomationRuleForm
              onSuccessfulSubmit={onSuccessfulSubmit}
              isPending={isPending}
              error={error}
              submitButtonContent={translate('Update Rule')}
              initialValue={initialRule}
              showUpdates
            />
          )}
        </ActionRightFlyoutContent>
      )}
    </RightFlyout>
  )
}

export default UpdateAutomationRule
